import React, { useState } from 'react';

import { link } from '@aline/routes';
import { IconButton, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlineTooltip, SmallCloseIconButton, Spacer } from 'src/components';
import type { AIReminderView } from 'src/components/ai/reminder-automation';
import { AIReminderAutomationList } from 'src/components/ai/reminder-automation';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { useBackToAI } from 'src/editor/toolbars/right-panel/BackToAIButton';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'auto',
    padding: theme.spacing(1),
    scrollbarWidth: 'thin',
  },
  header: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    gap: theme.spacing(0.5),
    height: 52,
    padding: theme.spacing(0, 1.5),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

const DRAWER_TITLES = {
  edit: 'Edit Reminder Automation',
  list: 'Automations',
  new: 'New Reminder Automation',
} satisfies Record<AIReminderView, string>;

interface ReportAutomationsContentProps {
  reportId: UUID;
}

export function ReportAutomationsContent({ reportId }: ReportAutomationsContentProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const handleBackToAI = useBackToAI();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<AIReminderView>('list');
  const [highlightAutomationId] = useState(() => searchParams.get('highlightAutomation'));

  const handleClose = () => {
    navigate(link.ReportDetail({ reportId }), { replace: true });
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        {view === 'list' ? (
          <AlineTooltip arrow placement="top" title="Back to AI">
            <IconButton size="small" onClick={handleBackToAI}>
              <BackArrowIcon />
            </IconButton>
          </AlineTooltip>
        ) : (
          <IconButton size="small" onClick={() => setView('list')}>
            <BackArrowIcon />
          </IconButton>
        )}
        <Typography className={classes.title} variant="subtitle1">
          {DRAWER_TITLES[view]}
        </Typography>
        <Spacer />
        {view === 'list' && <SmallCloseIconButton onClick={handleClose} />}
      </div>
      <div className={classes.content}>
        <AIReminderAutomationList
          highlightAutomationId={highlightAutomationId}
          reportId={reportId}
          setView={setView}
          view={view}
        />
      </div>
    </div>
  );
}
